import { Component, Input } from '@angular/core';
import { BadgeItemModel } from '../achievements.model';
import { BADGE_LOCKED_BG, BADGE_LOCKED_ICON, badgeIconClass, tierColor } from '../badge-visuals';

export type BadgeTileSize = 'xs' | 'sm' | 'md' | 'lg';

const TILE_PX: { [size in BadgeTileSize]: number } = { xs: 32, sm: 48, md: 64, lg: 84 };

/**
 * Square badge icon colored by tier; locked badges render gray, filled in color from the bottom
 * up as far as their progress goes. Purely presentational, shared by the achievements grid,
 * profile cards and dialogs.
 */
@Component({
  selector: 'app-badge-tile',
  templateUrl: './badge-tile.component.html',
  styleUrls: ['./badge-tile.component.scss'],
})
export class BadgeTileComponent {
  @Input() badge: BadgeItemModel;
  @Input() size: BadgeTileSize = 'md';
  /** Force the earned look (used by pickers that only list earned badges). */
  @Input() earned?: boolean;
  /** Fill a locked badge up to its progress; off where a locked badge just shows gray. */
  @Input() showProgress = true;

  get isEarned(): boolean {
    return this.earned !== undefined ? this.earned : !!this.badge?.isEarned;
  }

  get px(): number {
    return TILE_PX[this.size] || TILE_PX.md;
  }

  /** An uploaded SVG replaces the colored square and icon. */
  get hasArt(): boolean {
    return !!this.badge?.iconUrl;
  }

  get hasFill(): boolean {
    return !this.isEarned && this.showProgress && this.progressPercent > 0;
  }

  /** Hides the top of the colored copy, leaving the reached share visible at the bottom. */
  get fillClipPath(): string {
    return `inset(${100 - this.progressPercent}% 0 0 0)`;
  }

  /** Legacy icons fill with the tier's color (the primary color while no tier is known). */
  get fillColor(): string {
    return tierColor(this.badge?.currentTier);
  }

  get iconClass(): string {
    return badgeIconClass(this.badge?.iconKey);
  }

  get background(): string {
    return this.isEarned ? tierColor(this.badge?.currentTier) : BADGE_LOCKED_BG;
  }

  get iconColor(): string {
    return this.isEarned ? '#ffffff' : BADGE_LOCKED_ICON;
  }

  /** Repeat count shown as a corner pill (x2, x3, ...); hidden for single awards. */
  get repeatCount(): number {
    return this.isEarned && this.badge?.awardCount > 1 ? this.badge.awardCount : 0;
  }

  get progressPercent(): number {
    if (!this.badge || this.badge.progressTarget <= 0) {
      return 0;
    }
    return Math.min(100, (this.badge.progressCurrent / this.badge.progressTarget) * 100);
  }
}
